import { CurrencyAmount, Token } from '@kodiak-finance/sdk-core'
import BAULT_ABI from 'abis/Bault.json'
import { WAGMI_REFETCH_POLICY_NEVER } from 'config/RefetchPolicies'
import { useMemo } from 'react'
import type { Abi } from 'viem'
import { Address } from 'viem'

import { useReadContractMultiple } from './useReadContractMultiple'

/**
 * Hook to calculate the actual fee percentage for a specific withdrawal amount
 * Uses both convertToAssets and previewRedeem to determine the actual fee applied
 */
export const useActualFeePct = (
  baultAddress: Address | undefined,
  withdrawAmount: CurrencyAmount<Token> | undefined
) => {
  const contracts = useMemo(() => {
    if (!baultAddress || !withdrawAmount || withdrawAmount.equalTo('0')) return []
    return [
      {
        address: baultAddress,
        abi: BAULT_ABI as Abi,
        functionName: 'convertToAssets',
        args: [BigInt(withdrawAmount.quotient.toString())],
      },
      {
        address: baultAddress,
        abi: BAULT_ABI as Abi,
        functionName: 'previewRedeem',
        args: [BigInt(withdrawAmount.quotient.toString())],
      },
    ]
  }, [baultAddress, withdrawAmount])

  const {
    data: results,
    isLoading,
    isError,
  } = useReadContractMultiple({
    contracts,
    query: {
      ...WAGMI_REFETCH_POLICY_NEVER,
      enabled: contracts.length > 0,
    },
  })

  const feePct = useMemo(() => {
    if (!withdrawAmount || withdrawAmount.equalTo('0') || !results || results.length < 2) return undefined

    const [convertToAssetsResult, previewRedeemResult] = results

    if (
      convertToAssetsResult?.status === 'success' &&
      convertToAssetsResult.result &&
      previewRedeemResult?.status === 'success' &&
      previewRedeemResult.result
    ) {
      const assetsWithoutFee = convertToAssetsResult.result as bigint
      const actual = previewRedeemResult.result as bigint

      if (assetsWithoutFee === 0n) return 0

      const assetsWithoutFeeNum = Number(assetsWithoutFee)
      const actualNum = Number(actual)

      const feePercentage = ((assetsWithoutFeeNum - actualNum) / assetsWithoutFeeNum) * 100

      return feePercentage
    }

    return undefined
  }, [withdrawAmount, results])

  return {
    isLoading,
    error: isError,
    feePct,
  }
}
